import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, 
  Typography, 
  Paper, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  Box,
  Tooltip,
  Breadcrumbs,
  Link,
  Chip,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon, Add as AddIcon, Code, Visibility, NavigateNext } from '@mui/icons-material';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { apiService } from '../services/apiService';
import { useUser } from '../contexts/UserContext';
import { format } from 'date-fns';
import { 
  formatToEasternTime, 
  isoToEasternDate, 
  fromEasternTimeToUTC,
  TIMEZONE,
  treatLocalAsEastern
} from '../utils/dateUtils';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import PageContainer from '../components/PageContainer';
import LoadingState from '../components/LoadingState';
import EmailPreviewModal from '../components/EmailPreviewModal';

// Define email types
interface Email {
  id: number;
  name: string;
  subject: string;
  segment_name: string;
  html: string;
  scheduled_date: string;
  is_published: boolean;
  created_at: string;
  updated_at: string;
}

// New email form interface
interface EmailFormData {
  name: string;
  subject: string;
  segment_name: string;
  html: string;
  scheduled_date: string;
  is_published: boolean;
}

// Custom date picker component to avoid type errors
const CustomDatePicker: React.FC<{
  selected: Date;
  onChange: (date: Date | null) => void;
  dateFormat?: string;
  className?: string;
  placeholderText?: string;
}> = ({ selected, onChange, dateFormat, className, placeholderText }) => {
  // Simple date picker without time selection
  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <DatePicker
        selected={selected}
        onChange={onChange}
        dateFormat={dateFormat || "MMMM d, yyyy"}
        className={className}
        placeholderText={placeholderText}
        popperProps={{
          positionFixed: true
        }}
        popperModifiers={[
          {
            name: 'preventOverflow',
            options: {
              enabled: true,
              escapeWithReference: false,
              boundary: window
            },
          },
        ]}
      />
    </div>
  );
};

// Add this component for email content display
export const EmailSandbox: React.FC<{ html: string }> = ({ html }) => {
  return (
    <div
      style={{
        width: '100%',
        overflow: 'auto',
        backgroundColor: 'white',
        padding: '1rem'
      }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

// Format date for display
const formatDate = (dateString: string) => {
  return format(new Date(dateString), 'MMMM d, yyyy');
};

// Get color for segment
const getSegmentColor = (segment_name: string) => {
  switch (segment_name?.toLowerCase()) {
    case 'newsletter':
      return '#3b82f6'; // blue
    case 'luxury':
      return '#9333ea'; // purple
    case 'romance':
      return '#ec4899'; // pink
    case 'family':
      return '#22c55e'; // green
    case 'ultralux':
      return '#f59e0b'; // amber
    case 'unsubscribed':
      return '#9ca3af'; // light grey
    default:
      return '#3174ad'; // default blue
  }
};

// Truncate text with ellipsis
const truncateText = (text: string, maxLength: number): string => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

const Emails: React.FC = () => {
  const auth = useUser();
  const { selectedClientId } = useUser();
  const { emailId } = useParams<{ emailId?: string }>();
  const navigate = useNavigate();
  
  const [emails, setEmails] = useState<Email[]>([]);
  const [filteredEmails, setFilteredEmails] = useState<Email[]>([]);
  const [emailDetail, setEmailDetail] = useState<Email | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDetailLoading, setIsDetailLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFilter, setShowFilter] = useState<'all' | 'future'>('future');
  
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });
  
  // Email preview modal state
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewEmail, setPreviewEmail] = useState<Email | null>(null);
  
  // Fetch all emails
  const fetchEmails = useCallback(async () => {
    if (!selectedClientId || !auth.isAuthenticated) {
      console.log('No selectedClientId or not authenticated, skipping data fetch');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const response = await apiService.get('/api/emails', { 
        params: { 
          client_id: selectedClientId,
          include_drafts: false  // Only show published emails
        } 
      });
      const allEmails = response.data as Email[];
      setEmails(allEmails);
      filterEmails(allEmails, showFilter);
    } catch (error) {
      console.error('Error fetching emails:', error);
      setError('Failed to load emails. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  }, [selectedClientId, showFilter, auth.isAuthenticated]);
  
  // Filter emails based on date
  const filterEmails = (emailsToFilter: Email[], filter: 'all' | 'future') => {
    if (filter === 'all') {
      setFilteredEmails(emailsToFilter);
      return;
    }
    
    // For 'future', only show emails scheduled for today or later
    const now = new Date();
    now.setHours(0, 0, 0, 0); // Set to beginning of today
    
    const futureEmails = emailsToFilter.filter(email => {
      const emailDate = new Date(email.scheduled_date);
      emailDate.setHours(0, 0, 0, 0); // Set to beginning of the day
      return emailDate >= now;
    });
    
    setFilteredEmails(futureEmails);
  };
  
  // Handle filter change
  const handleFilterChange = (_: React.MouseEvent<HTMLElement>, newFilter: 'all' | 'future' | null) => {
    if (newFilter !== null) {
      setShowFilter(newFilter);
      filterEmails(emails, newFilter);
    }
  };
  
  // Fetch a single email's details
  const fetchEmailDetail = useCallback(async (id: number) => {
    if (!selectedClientId || !auth.isAuthenticated) {
      console.log('No selectedClientId or not authenticated, skipping email detail fetch');
      return;
    }

    try {
      setIsDetailLoading(true);
      setError(null);
      
      // First try to find the email in our existing list
      const existingEmail = emails.find(email => email.id === id);
      if (existingEmail) {
        console.log('Found email in existing list:', existingEmail);
        console.log('HTML content length:', existingEmail.html?.length || 0);
        setEmailDetail(existingEmail);
        return;
      }

      // If not found in existing list, fetch the specific email
      const response = await apiService.get<Email>(`/api/emails/${id}`, { 
        params: { 
          client_id: selectedClientId
        } 
      });
      
      if (response.data) {
        console.log('Found email:', response.data);
        console.log('HTML content length:', response.data.html?.length || 0);
        setEmailDetail(response.data);
      } else {
        setError('Email not found');
      }
    } catch (error) {
      console.error(`Error fetching email with ID ${id}:`, error);
      setError('Failed to load email details. Please try again later.');
    } finally {
      setIsDetailLoading(false);
    }
  }, [selectedClientId, auth.isAuthenticated, emails]);
  
  // Initial data loading
  useEffect(() => {
    fetchEmails();
  }, [fetchEmails]);
  
  // Apply filter when emails or filter changes
  useEffect(() => {
    filterEmails(emails, showFilter);
  }, [emails, showFilter]);
  
  // Load email detail when emailId changes
  useEffect(() => {
    if (emailId) {
      const id = parseInt(emailId, 10);
      if (!isNaN(id)) {
        fetchEmailDetail(id);
      } else {
        setError('Invalid email ID');
      }
    } else {
      setEmailDetail(null);
    }
  }, [emailId, fetchEmailDetail]);
  
  // Handle row click to navigate to email detail
  const handleRowClick = (emailId: number) => {
    navigate(`/app/emails/${emailId}`);
  };
  
  // Handle opening email preview modal
  const handleOpenEmailPreview = async (email: Email) => {
    try {
      // Fetch full email details including HTML content
      const response = await apiService.get<Email>(`/api/emails/${email.id}`);
      if (response.data) {
        setPreviewEmail(response.data);
        setPreviewModalOpen(true);
      }
    } catch (error) {
      console.error('Error fetching email details:', error);
      // Fallback to basic email info if API call fails
      setPreviewEmail(email);
      setPreviewModalOpen(true);
    }
  };
  
  // Handle closing email preview modal
  const handleCloseEmailPreview = () => {
    setPreviewModalOpen(false);
    setPreviewEmail(null);
  };
  
  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };
  
  // Email List View
  const renderEmailList = () => {
    return (
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Subject</TableCell>
              <TableCell>Segment</TableCell>
              <TableCell>Scheduled Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredEmails
              .sort((a, b) => new Date(a.scheduled_date).getTime() - new Date(b.scheduled_date).getTime())
              .map((email) => (
              <TableRow 
                key={email.id} 
                className="hover:bg-gray-50"
                sx={{ height: '48px' }}
              >
                <TableCell padding="normal" sx={{ py: 1 }}>
                  <Link
                    component="button"
                    variant="body2"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenEmailPreview(email);
                    }}
                    sx={{ 
                      textDecoration: 'none',
                      color: 'primary.main',
                      '&:hover': {
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    {email.name}
                  </Link>
                </TableCell>
                <TableCell padding="normal" sx={{ py: 1 }}>
                  <Tooltip title={email.subject || '-'} arrow placement="top">
                    <span>{truncateText(email.subject, 50) || '-'}</span>
                  </Tooltip>
                </TableCell>
                <TableCell padding="normal" sx={{ py: 1 }}>
                  <Chip
                    label={email.segment_name ? email.segment_name.charAt(0).toUpperCase() + email.segment_name.slice(1) : 'No Segment'}
                    size="small"
                    sx={{
                      backgroundColor: getSegmentColor(email.segment_name),
                      color: 'white',
                      height: '24px',
                      minWidth: '80px',
                      '& .MuiChip-label': {
                        px: 1,
                        display: 'block',
                        whiteSpace: 'nowrap',
                        overflow: 'visible'
                      }
                    }}
                  />
                </TableCell>
                <TableCell padding="normal" sx={{ py: 1 }}>{formatDate(email.scheduled_date)}</TableCell>
                <TableCell padding="normal" sx={{ py: 1 }}>
                  <Tooltip title="View Email Details">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenEmailPreview(email);
                      }}
                    >
                      <Visibility />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {filteredEmails.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-4">
                  <Typography variant="body1" color="textSecondary">
                    {showFilter === 'future' ? 'No future emails found.' : 'No emails found.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };
  
  // Email Detail View
  const renderEmailDetail = () => {
    if (isDetailLoading) {
      return (
        <LoadingState 
          message="Loading email details..." 
          variant="section"
          size="medium"
        />
      );
    }
    
    if (error) {
      return <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>;
    }
    
    if (!emailDetail) {
      return <Alert severity="warning" sx={{ mt: 2 }}>Email not found</Alert>;
    }

    console.log('Rendering email detail:', emailDetail);
    console.log('HTML content to render:', emailDetail.html);
    
    return (
      <>
        {/* Email Details */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, mb: 3 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Scheduled Date
              </Typography>
              <Typography variant="body1">
                {formatDate(emailDetail.scheduled_date)}
              </Typography>
            </Box>
            
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Segment
              </Typography>
              <Chip
                label={emailDetail.segment_name ? emailDetail.segment_name.charAt(0).toUpperCase() + emailDetail.segment_name.slice(1) : 'No Segment'}
                size="small"
                sx={{
                  backgroundColor: getSegmentColor(emailDetail.segment_name),
                  color: 'white',
                  mt: 0.5,
                  minWidth: '80px',
                  '& .MuiChip-label': {
                    px: 1,
                    display: 'block',
                    whiteSpace: 'nowrap',
                    overflow: 'visible'
                  }
                }}
              />
            </Box>
            
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Subject
              </Typography>
              <Typography variant="body1">
                {emailDetail.subject || '-'}
              </Typography>
            </Box>
          </Box>
          
          <Typography variant="h5" sx={{ mt: 4, mb: 2 }}>
            Email Preview
          </Typography>
          
          {/* Full Email Content */}
          <Box sx={{ 
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            mb: 4
          }}>
            <EmailSandbox html={emailDetail.html} />
          </Box>
        </Box>
      </>
    );
  };
  
  return (
    <PageContainer>
      {emailId ? (
        <Breadcrumbs 
          separator={<NavigateNext fontSize="small" />} 
          aria-label="breadcrumb"
          sx={{ mb: 3 }}
        >
          <Link 
            component={RouterLink} 
            to="/app/emails" 
            color="inherit"
            sx={{ 
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
          >
            Emails
          </Link>
          <Typography color="text.secondary">Email Detail</Typography>
        </Breadcrumbs>
      ) : (
        <Typography 
          variant="subtitle1" 
          color="text.secondary"
          sx={{ mb: 3 }}
        >
          Emails
        </Typography>
      )}

      <Paper elevation={2} className="p-6">
        {emailId ? renderEmailDetail() : (
          <>
            <Box className="flex justify-end items-center mb-4">
              <ToggleButtonGroup
                value={showFilter}
                exclusive
                onChange={handleFilterChange}
                aria-label="email filter"
                size="small"
              >
                <ToggleButton value="future" aria-label="show future emails">
                  Show Future
                </ToggleButton>
                <ToggleButton value="all" aria-label="show all emails">
                  Show All
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            
            {isLoading ? (
              <LoadingState 
                message="Loading emails..." 
                variant="page"
                size="medium"
              />
            ) : error ? (
              <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
            ) : (
              <Box sx={{ 
                bgcolor: 'grey.50',
                borderRadius: 1,
                p: 2
              }}>
                {renderEmailList()}
              </Box>
            )}
          </>
        )}
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Email Preview Modal */}
      <EmailPreviewModal
        open={previewModalOpen}
        onClose={handleCloseEmailPreview}
        email={previewEmail ? {
          name: previewEmail.name,
          subject: previewEmail.subject,
          html: previewEmail.html,
          scheduled_date: previewEmail.scheduled_date,
          agency_name: '', // We don't have agency name in the Email interface
          segment_name: previewEmail.segment_name,
          existing_tags: [] // We don't have tags in the Email interface
        } : null}
      />
    </PageContainer>
  );
};

export default Emails;
